import {
	login,
	loginAccept,
	LoginAcceptRequest,
	loginAuth,
	LoginByPhoneRequest,
	loginPre,
	LoginPreRequest,
	LoginRequest,
	LoginResponse,
} from '@api/FW/auth.api';
import { clearAnalyticsIdentity } from '@app/services/analytics.service';
import {
	deleteLocalStorageData,
	readLocalStorageData,
	saveLocalStorageData,
} from '@app/services/localStorage.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { StdApiError } from '@interfaces/FW/StdApiError';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { setUserPermissions } from './userSlice';

export interface AuthSlice {
	token: string | null;
	isAuth: boolean;
}

const initialState: AuthSlice = {
	token: readLocalStorageData(LocalStorageItems.sessionID),
	isAuth: readLocalStorageData(LocalStorageItems.isAuth),
};

export const doLogin = createAsyncThunk<LoginResponse, LoginRequest, { rejectValue: StdApiError }>(
	'auth/login',
	async (loginPayload, { dispatch, rejectWithValue }) => {
		try {
			const res = await login(loginPayload);

			saveLocalStorageData(LocalStorageItems.sessionID, res.token);
			saveLocalStorageData(LocalStorageItems.OilWalletsUserId, res.OilWalletsUserId);

			dispatch(setUserPermissions(res.user));

			if (!res.needSecondStep) {
				saveLocalStorageData(LocalStorageItems.isAuth, res.isAuth);
			}

			return res;
		} catch (err) {
			return rejectWithValue(err as StdApiError);
		}
	},
);

export const doLoginAccept = createAsyncThunk(
	'auth/loginAccept',
	async (loginPayload: LoginAcceptRequest, { dispatch }) =>
		loginAccept(loginPayload).then((res) => {
			saveLocalStorageData(LocalStorageItems.isAuth, res.isAuth);
			if (res.user) dispatch(setUserPermissions(res.user));
			return res;
		}),
);

export const doLoginPre = createAsyncThunk<void, LoginPreRequest, { rejectValue: StdApiError }>(
	'auth/loginPre',
	async (loginPayload, { rejectWithValue }) => {
		try {
			await loginPre(loginPayload);
		} catch (err) {
			return rejectWithValue(err as StdApiError);
		}
	},
);

export const doLoginByPhone = createAsyncThunk<
	LoginResponse,
	LoginByPhoneRequest,
	{ rejectValue: StdApiError }
>('auth/loginByPhone', async (loginPayload, { dispatch, rejectWithValue }) => {
	try {
		const res = await loginAuth(loginPayload);

		saveLocalStorageData(LocalStorageItems.sessionID, res.token);
		saveLocalStorageData(LocalStorageItems.OilWalletsUserId, res.OilWalletsUserId);
		saveLocalStorageData(LocalStorageItems.isAuth, res.isAuth);

		if (res.user) dispatch(setUserPermissions(res.user));

		return res;
	} catch (err) {
		return rejectWithValue(err as StdApiError);
	}
});

export const doLogout = createAsyncThunk('auth/doLogout', (payload, { dispatch }) => {
	document.title = 'WOG-ПК';

	// Every sign-out path (manual logout, the login-page cleanup, the 401
	// interceptor) dispatches this thunk, so the analytics identity resets here, once.
	clearAnalyticsIdentity();

	deleteLocalStorageData(LocalStorageItems.OilWalletsUserId);
	deleteLocalStorageData(LocalStorageItems.sessionID);
	deleteLocalStorageData(LocalStorageItems.userPermissions);
	deleteLocalStorageData(LocalStorageItems.wallets);
	deleteLocalStorageData(LocalStorageItems.isAuth);
	deleteLocalStorageData(LocalStorageItems.client);
	deleteLocalStorageData(LocalStorageItems.currentAccount);
	deleteLocalStorageData(LocalStorageItems.selectedClient);
	deleteLocalStorageData(LocalStorageItems.allClients);
});

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(doLogin.fulfilled, (state, action) => {
			state.token = action.payload.token;
			state.isAuth = action.payload.isAuth;
		});
		builder.addCase(doLoginAccept.fulfilled, (state, action) => {
			state.isAuth = action.payload.isAuth;
		});
		builder.addCase(doLoginByPhone.fulfilled, (state, action) => {
			state.token = action.payload.token;
			state.isAuth = action.payload.isAuth;
		});
		builder.addCase(doLogout.fulfilled, (state) => {
			state.token = '';
			state.isAuth = false;
		});
	},
});

export default authSlice.reducer;
